import { BOTE, HEROES } from '@somnhold/content';
import { cellId } from '../grid.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { BoatRules, BoteRules, ElderRules, SwellRules, WakeRules } from '../bote-types.ts';
import type { Hex } from '../hex.ts';
import { BOTE_RADIUS, type BoatLayout, type ElderLayout, type SwellLayout, type WakeLayout } from './bote-layout.ts';
import { wakeGoal, wakeHold, wakeOrder, wakeResult, wakeTick } from './wake.ts';
import { swellGoal, swellHold, swellOrder, swellResult, swellTick } from './swell.ts';
import { elderGoal, elderHold, elderResult, elderTick } from './elder.ts';

/**
 * The Long Hunt's chapter 3 (docs/sendas/barbaro.md, «El bote»): its rules from their layouts (the fight's builder and
 * the renderer's scenes build them the same way; lives in hit points worked out by the caller, seconds of D) and the one
 * door battle/senda.ts goes through for its three acts (`rules.senda.bote`).
 */

const R = BOTE_RADIUS + 2;
const ids = (hs: Hex[]): number[] => hs.map((h) => cellId(R, h.q, h.r)).filter((c) => c >= 0).sort((a, b) => a - b);

/** The six rowers, oar by oar (bow first, west then east): four of the clan, Sten and Terrón («ahora es remero»). */
export const BOTE_ROWERS = ['rower1', 'rower2', 'sten', 'terron', 'rower3', 'rower4'];

export const boatRules = (L: BoatLayout): BoatRules => ({
  cells: ids(L.hull),
  mid: { ...L.mid },
  stern: L.stern,
  bow: L.bow,
  oars: L.oars.map((p) => ({ ...p })),
  rowers: BOTE_ROWERS.slice(),
  shields: L.shields.map((p) => ({ ...p })),
  tiller: { ...L.tiller },
  gnaw: BOTE.boat.gnaw,
  gnawReach: BOTE.boat.gnawReach,
});

export const wakeRules = (L: WakeLayout, wolfHp: number): WakeRules => {
  const W = BOTE.wake;
  return {
    boat: boatRules(L.boat),
    forks: L.forks.map((f) => ({ channels: f.channels.map((c) => ({ ...c })), right: f.right, wide: f.wide })),
    first: W.first,
    look: W.look,
    pass: W.pass,
    gap: W.gap,
    start: W.start,
    row: W.row,
    drag: W.drag,
    gain: W.gain,
    wrong: W.wrong,
    ambush: W.ambush,
    stall: W.stall,
    crush: W.crush,
    stallWolves: W.stallWolves,
    climb: { first: W.climb.first, every: [W.climb.every[0], W.climb.every[1]], n: [W.climb.n[0], W.climb.n[1]] },
    bite: W.bite,
    biteReach: W.biteReach,
    spare: W.spare,
    spares: W.spares,
    ivar: { ...W.ivar },
    crows: { ...W.crows },
    wolfHp,
  };
};

export const swellRules = (L: SwellLayout, wolfHp: number): SwellRules => {
  const W = BOTE.swell;
  const pairs: [number, number][] = [];
  L.floeCells.forEach((f, k) => {
    for (const c of ids(f)) pairs.push([c, k]);
  });
  pairs.sort((a, b) => a[0] - b[0]);
  return {
    boat: boatRules(L.boat),
    floes: L.floes.map((p) => ({ ...p })),
    floeCells: pairs.map((p) => p[0]),
    floeOf: pairs.map((p) => p[1]),
    rope: W.rope,
    ropeReach: W.ropeReach,
    plan: L.plan.slice(),
    warn: W.warn,
    need: W.need,
    push: W.push,
    fallBp: W.fallBp,
    stun: W.stun,
    hold: W.hold,
    haul: W.haul,
    haulT: W.haulT,
    lostMax: W.lostMax,
    shaken: W.shaken,
    climb: { first: W.climb.first, every: [W.climb.every[0], W.climb.every[1]], n: [W.climb.n[0], W.climb.n[1]] },
    floe: { first: W.floe.first, every: [W.floe.every[0], W.floe.every[1]], n: [W.floe.n[0], W.floe.n[1]], gather: W.floe.gather },
    crows: { ...W.crows },
    wolfHp,
  };
};

export const elderRules = (L: ElderLayout, wolfHp: number, elderHp: number): ElderRules => {
  const E = BOTE.elder;
  return {
    boat: boatRules(L.boat),
    floes: L.floes.map((p) => ({ ...p })),
    tips: L.tips.map((p) => ({ ...p })),
    deep: L.deep.map((p) => ({ ...p })),
    dark: L.dark.slice(),
    shift: E.shift,
    hp: elderHp,
    biteBp: E.biteBp,
    reload: E.reload,
    reach: E.reach,
    fleeBp: E.fleeBp,
    look: E.look,
    bound: E.bound,
    speed: HEROES.barbarian.speed,
    slowBp: E.slowBp,
    block: E.block,
    caught: E.caught,
    stun: E.stun,
    under: E.under,
    healBp: E.healBp,
    raise: E.raise,
    leapEvery: E.leapEvery,
    leapFirst: E.leapFirst,
    leapWarn: E.leapWarn,
    leapBp: E.leapBp,
    leapR: E.leapR,
    leapHull: E.leapHull,
    pack: { first: E.pack.first, every: [E.pack.every[0], E.pack.every[1]], n: [E.pack.n[0], E.pack.n[1]] },
    crows: { ...E.crows },
    wolfHp,
  };
};

const br = (s: BattleState): BoteRules | undefined => s.rules?.senda?.bote;

export const boteTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = br(s);
  if (r?.wake) wakeTick(s, cue);
  else if (r?.swell) swellTick(s, cue);
  else if (r?.elder) elderTick(s, cue);
};

/** The hero's tap on one of the act's marks. */
export const boteOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = br(s);
  if (r?.wake) wakeOrder(s, h, at);
  else if (r?.swell) swellOrder(s, h, at);
};

export const boteGoal = (s: BattleState): { done: number; need: number } => {
  const r = br(s);
  return r?.wake ? wakeGoal(s) : r?.swell ? swellGoal(s) : elderGoal(s);
};

export const boteResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = br(s);
  return r?.wake ? wakeResult(s) : r?.swell ? swellResult(s) : r?.elder ? elderResult(s) : null;
};

/** The act moved this nightmare itself (true). */
export const boteHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = br(s);
  if (r?.wake) return wakeHold(s, e, px);
  if (r?.swell) return swellHold(s, e);
  if (r?.elder) return elderHold(s, e, px);
  return false;
};
